import { uniq } from 'lodash';

import { useStoreActions } from '~/store/hooks';
import api from '~/services/api';
import {
  adjustFormValuesByCrisisAllocations,
  groupClientAllocationData,
} from '~/utils/clientAllocation';
import { extractErrorMessage } from '~/utils/error/error';

import { IUpdateClientAllocation } from '~/services/api/clientAllocation';
import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
} from '~/services/api/clientAllocation/types';
import { IClientOption, IClientOptionByTeam } from '~/store/client/types';
import { normalizeDateString } from '~/utils/date/date';
import extractFullName from '~/utils/text/extractFullName';
import { AllocationMode } from '../constants/allocationMode';
import excludeArchivedClients from '../helpers/excludeArchivedClients';
import extractArchivedClients from '../helpers/extractArchivedClients';
import extractLocationGroupTeamIdsByClients from '../helpers/extractLocationGroupTeamIdsByClients';
import { getClientMemberFormFieldName, getClientsFormFieldName } from '../helpers/getters';
import findChangedAllocations from '../helpers/findChangedAllocations';

interface IUseEditClientAllocationProps {
  clinicId: string;
  isGlobalUser: boolean;
  isActTeamMember: boolean;
  clientAllocation: IClientAllocationDetailed[];
  actTeamId: string;
  current: IClientOptionByTeam[];
  locationGroups: IAllocationClientsByGroup[];
  dirtyFields: Record<string, unknown>;
  isCrisis: boolean;
}

const useEditClientAllocation = ({
  clinicId,
  isGlobalUser,
  isActTeamMember,
  clientAllocation,
  actTeamId,
  current,
  locationGroups,
  dirtyFields,
  isCrisis,
}: IUseEditClientAllocationProps): any => {
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onEditCrisisAllocation, onDeleteCrisisAllocation } = useStoreActions(
    actions => actions.crisis,
  );

  const editAllocationIds = Array.from(
    new Set(
      Object.keys(dirtyFields).flatMap(dirtyKey => {
        const numbers = dirtyKey.match(/\d+/g);
        return numbers ? [numbers[numbers.length - 1]] : [];
      }),
    ),
  );

  const handleEditCrisisAllocations = async (
    adjustedFormValues: Record<string, string>,
    crisisAllocations: IClientAllocationDetailed[],
  ) => {
    try {
      await Promise.all(
        crisisAllocations.map(async allocation => {
          if (allocation.user.clients?.length === 0) {
            await onDeleteCrisisAllocation({
              clinicId,
              id: allocation.id,
            });
            return;
          }
          allocation.user.clients.forEach(client => {
            const memoFormValue =
              adjustedFormValues[
                getClientMemberFormFieldName(
                  'memo_',
                  client.id,
                  getClientsFormFieldName(allocation.user.id, allocation.id),
                )
              ] || undefined;
            onEditCrisisAllocation({
              clinicId,
              id: String(allocation.id),
              memo: memoFormValue as string,
            });
          });
        }),
      );
      showNotify({ message: 'Client allocation successfully updated' });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  // global user has different endpoints and can create client allocations for any client in the clinic
  // at the same time global user has permissions as a team member, so can manage only own allocations
  const handleGlobalUserAllocations = async (
    clientAllocations: IClientAllocationDetailed[],
    allocation: IUpdateClientAllocation[],
    mode: AllocationMode,
    values: Record<string, any>,
  ) => {
    const allocationTeams = clientAllocations?.map(item => String(item.team.id));
    const allocatedClients = clientAllocations?.flatMap(item => item.user?.clients?.map(c => c.id));

    let selectedTeams = current.reduce((acc: { [key: number]: IClientOption[] }, obj) => {
      const allocatedTeam = obj.teams.find(t => allocatedClients.includes(t.id));
      const selectedTeam = allocatedTeam || obj.teams.find(team => team.isPrimary);

      acc[selectedTeam.id] = [...(acc[selectedTeam.id] || []), obj];

      return acc;
    }, {});

    if (mode === AllocationMode.BY_LOCATION_GROUP) {
      const selectedClientIds = allocation?.flatMap(a => a.clients.map(c => c.id));
      selectedTeams = extractLocationGroupTeamIdsByClients(locationGroups, selectedClientIds);
    }

    const allocationsForDeleteGU = clientAllocations
      .filter(item => !selectedTeams[item.team.id])
      ?.map(item => String(item.id));

    const allocationTeamsForAddGU = Object.keys(selectedTeams).filter(
      key => !clientAllocations.find(a => a.team.id === Number(key)),
    );
    const allocationForEditGU = Object.keys(selectedTeams).filter(item =>
      allocationTeams.includes(item),
    );

    if (
      !allocationTeamsForAddGU.length &&
      !allocationsForDeleteGU.length &&
      !allocationForEditGU.length
    ) {
      return;
    }

    try {
      if (allocationsForDeleteGU.length) {
        await Promise.all(
          allocationsForDeleteGU?.map((clientAllocationId: string) =>
            api.clientAllocation.deleteOwnClientAllocationGU({
              clinicId,
              clientAllocationId,
            }),
          ),
        );
      }
      const addPayload = {
        from: normalizeDateString(clientAllocation[0].date),
        to: normalizeDateString(clientAllocation[0].date, true),
        includeWeekends: true,
        locationGroupIds: allocation?.flatMap(item => item.locationGroupIds),
      };

      if (allocationTeamsForAddGU.length) {
        let clients = [];

        if (mode === AllocationMode.BY_CLIENT) {
          clients = current.filter(client =>
            allocationTeamsForAddGU.some(teamId =>
              client.teams.some(team => team.id === Number(teamId)),
            ),
          );
        } else {
          clients = allocation[0].clients;
        }

        if (clients.length) {
          await api.clientAllocation.addOwnClientAllocationGU(
            { clinicId },
            {
              ...addPayload,
              clients: allocation[0].clients.filter(c => !allocatedClients.includes(c.id)),
            },
          );
        }
      }

      if (allocationForEditGU.length) {
        await Promise.all(
          allocationForEditGU?.map(teamId => {
            const currentClientAllocation = clientAllocations.find(
              item => String(item.team.id) === teamId,
            );
            const allocationId = currentClientAllocation?.id;
            const memberId = currentClientAllocation?.user.id;
            const locationGroupIds = allocation
              .filter(item => item.id === String(allocationId))
              ?.flatMap(item => item.locationGroupIds);

            let clients = [];

            if (mode === AllocationMode.BY_CLIENT) {
              clients = current.filter(client =>
                client.teams.some(team => team.id === Number(teamId)),
              );
            } else {
              clients = selectedTeams[Number(teamId)];
            }

            return api.clientAllocation.updateOwnClientAllocationGU(
              { clinicId, teamId: actTeamId, clientAllocationId: String(allocationId) },
              {
                ...addPayload,
                clients: clients.map(client => {
                  const clientTeamId =
                    values[
                      getClientMemberFormFieldName(
                        'teamId_',
                        client.id,
                        getClientsFormFieldName(memberId, allocationId),
                      )
                    ];

                  return {
                    id: client.id,
                    teamId: clientTeamId ? Number(clientTeamId) : null,
                    memo:
                      values[
                        getClientMemberFormFieldName(
                          'memo_',
                          client.id,
                          getClientsFormFieldName(memberId, allocationId),
                        )
                      ] || null,
                  };
                }),
                locationGroupIds,
              },
            );
          }),
        );
      }
      if (
        allocationsForDeleteGU.length ||
        allocationForEditGU.length ||
        allocationTeamsForAddGU.length
      ) {
        showNotify({ message: 'Client allocation successfully updated' });
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const handleEditClientAllocation = async (
    formValues: Record<string, string>,
    clientAllocations: IClientAllocationDetailed[],
    allocationMode: AllocationMode,
  ) => {
    const archivedClients = extractArchivedClients(clientAllocations);

    if (archivedClients.length) {
      const archivedClientsNames = uniq(
        archivedClients.map(client => extractFullName(client)),
      ).join(', ');
      const isMultipleArchived = archivedClients.length > 1;
      showError(
        `The archived client${isMultipleArchived ? 's' : ''} ${archivedClientsNames} ha${
          isMultipleArchived ? 've' : 's'
        } been removed from the created allocation`,
      );
    }

    try {
      if (archivedClients.length) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      // Union with ids from changedAllocations so that allocations where only teamId
      // changed (global user) still hit the edit branch in groupClientAllocationData.
      const effectiveEditAllocationIds = Array.from(
        new Set([...editAllocationIds, ...clientAllocations.map(a => String(a.id))]),
      );

      const {
        allocationsForAdd,
        allocationsForDelete,
        allocationsForEdit: allocationsForEditWithArchivedClients,
      } = groupClientAllocationData(
        formValues,
        isGlobalUser ? effectiveEditAllocationIds : editAllocationIds,
        clientAllocations,
      );
      const allocationsForEdit = excludeArchivedClients<IUpdateClientAllocation>(
        allocationsForEditWithArchivedClients,
        archivedClients,
      );

      if (!allocationsForAdd.length && !allocationsForDelete.length && !allocationsForEdit.length) {
        return false;
      }

      const addPayload = {
        from: normalizeDateString(clientAllocation[0].date),
        to: normalizeDateString(clientAllocation[0].date, true),
        includeWeekends: true,
        allocations: allocationsForAdd,
      };

      if (allocationsForAdd.length && !isGlobalUser) {
        await api.clientAllocation.addClientAllocation({ clinicId, teamId: actTeamId }, addPayload);
      }
      if (allocationsForEdit.length) {
        if (isGlobalUser) {
          // as global user can edit only own allocations (like team member), there won't be anything except allocationsForEdit
          await handleGlobalUserAllocations(
            clientAllocations,
            allocationsForEdit,
            allocationMode,
            formValues,
          );
        } else if (isActTeamMember) {
          await api.clientAllocation.updateOwnClientAllocation(
            { clinicId, teamId: actTeamId, clientAllocationId: allocationsForEdit[0].id },
            {
              ...addPayload,
              clients: allocationsForEdit[0].clients,
              locationGroupIds: allocationsForEdit[0].locationGroupIds,
            },
          );
        } else {
          await api.clientAllocation.updateClientAllocation(
            { clinicId, teamId: actTeamId },
            {
              allocations: allocationsForEdit,
            },
          );
        }
      }

      if (allocationsForDelete.length) {
        if (isActTeamMember || isGlobalUser) {
          const removeMethod = isGlobalUser
            ? api.clientAllocation.deleteOwnClientAllocationGU
            : api.clientAllocation.deleteOwnClientAllocation;

          await removeMethod({
            clinicId,
            teamId: actTeamId,
            clientAllocationId: String(allocationsForDelete[0]),
          });
        } else {
          await Promise.all(
            allocationsForDelete.map(id =>
              api.clientAllocation.deleteClientAllocation({
                clinicId,
                teamId: actTeamId,
                clientAllocationId: String(id),
              }),
            ),
          );
        }
      }

      if (allocationsForDelete.length || allocationsForEdit.length || allocationsForAdd.length) {
        showNotify({ message: 'Client allocation successfully updated' });
        return true;
      }
      return false;
    } catch (e) {
      showError(extractErrorMessage(e));
      return false;
    }
  };

  const onSubmit = async ({
    name,
    allocationMode,
    ...formValues
  }: { name: string; allocationMode: AllocationMode } & unknown) => {
    const adjustedFormValues = adjustFormValuesByCrisisAllocations(formValues, clientAllocation);
    const changedAllocations = findChangedAllocations(
      adjustedFormValues,
      clientAllocation,
      dirtyFields,
      isGlobalUser,
    );

    if (isCrisis) {
      // Edit crisis allocations
      const isSuccessEditCrisisAllocations = await handleEditCrisisAllocations(
        adjustedFormValues as Record<string, string>,
        changedAllocations,
      );
      return isSuccessEditCrisisAllocations;
    }

    // Edit client allocations
    const isSuccessEditClientAllocation = await handleEditClientAllocation(
      adjustedFormValues as Record<string, string>,
      changedAllocations,
      allocationMode,
    );

    return isSuccessEditClientAllocation;
  };

  return onSubmit;
};

export default useEditClientAllocation;
